import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { buildTourLocale, buildTranslatedSteps, createTourStyles, getAvailablePageTourSteps } from '../../config/tourSteps';
import TourTooltip from './TourTooltip';

const PageTourContext = createContext(null);

export const PageTourProvider = ({ children }) => {
  const theme = useTheme();
  const { t } = useTranslation('onboarding');
  const [currentPage, setCurrentPage] = useState(null);
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const tourKey = useRef(0);
  const registrations = useRef(new Map());

  const registerPage = useCallback((pageKey) => {
    registrations.current.set(pageKey, (registrations.current.get(pageKey) || 0) + 1);
    setCurrentPage(pageKey);

    return () => {
      const remaining = (registrations.current.get(pageKey) || 1) - 1;
      if (remaining > 0) {
        registrations.current.set(pageKey, remaining);
        return;
      }

      registrations.current.delete(pageKey);
      setCurrentPage((prev) => (prev === pageKey ? null : prev));
    };
  }, []);

  const startTour = useCallback((pageKey) => {
    const targetPage = pageKey || currentPage;
    const availableSteps = getAvailablePageTourSteps(targetPage);

    // Check targets at click time: data-dependent controls can appear after
    // the screen has registered itself with the provider.
    if (!availableSteps.length) return false;

    const translatedSteps = buildTranslatedSteps(availableSteps, t);

    tourKey.current += 1;
    setSteps(translatedSteps);
    setRun(true);
    return true;
  }, [currentPage, t]);

  const stopTour = useCallback(() => {
    setRun(false);
    setSteps([]);
  }, []);

  const handleCallback = useCallback((data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      stopTour();
    }
  }, [stopTour]);

  const hasSteps = Boolean(currentPage && getAvailablePageTourSteps(currentPage).length);

  const value = useMemo(() => ({
    currentPage,
    hasSteps,
    registerPage,
    startTour,
    stopTour,
  }), [currentPage, hasSteps, registerPage, startTour, stopTour]);
  const tourStyles = useMemo(() => createTourStyles(theme), [theme]);

  return (
    <PageTourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Joyride
          key={tourKey.current}
          run={run}
          steps={steps}
          callback={handleCallback}
          continuous
          disableCloseOnEsc={false}
          disableOverlayClose
          hideCloseButton={false}
          locale={buildTourLocale(t)}
          showProgress
          showSkipButton
          styles={tourStyles}
          tooltipComponent={TourTooltip}
          floaterProps={{ disableAnimation: true }}
        />
      )}
    </PageTourContext.Provider>
  );
};

export const usePageTourContext = () => {
  const context = useContext(PageTourContext);
  if (!context) {
    throw new Error('usePageTourContext must be used within PageTourProvider');
  }
  return context;
};

export default PageTourProvider;
